import React from "react";
import { Box, Typography } from "@mui/material";
import { COLORS } from "../../common/colors";
import { RECENCY_LOOKBACK_DAYS } from "src/providers/compliance/complianceClock";
import type { SwatchLevel } from "./complianceColors";
import { SWATCH_LEVEL_LABELS, swatchSx } from "./complianceColors";

interface TrafficLightProps {
  level: SwatchLevel;
  label: string;
  caption: string;
  // Read out instead of the level's label, e.g. for exempt streams.
  status?: string;
}

// No timestamp only means nothing within the lookback window, so "None yet" is only safe while
// the patient has been monitored for less than that.
export function recencyLabel(
  days: number | null | undefined,
  monitoredDays: number
): string {
  if (days === null || days === undefined) {
    return monitoredDays < RECENCY_LOOKBACK_DAYS
      ? "None yet"
      : `None in ${RECENCY_LOOKBACK_DAYS}d`;
  }
  if (days === 0) return "Today";
  if (days === 1) return "1d ago";
  return `${days}d ago`;
}

export function TrafficLight({
  level,
  label,
  caption,
  status = SWATCH_LEVEL_LABELS[level]
}: TrafficLightProps): JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "3px"
      }}
    >
      <Box
        role="img"
        aria-label={`${label}: ${status}`}
        sx={{
          width: 20,
          height: 20,
          ...swatchSx(level)
        }}
      />
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 500,
          color: COLORS.GREY_800,
          lineHeight: 1,
          whiteSpace: "nowrap"
        }}
      >
        {caption}
      </Typography>
    </Box>
  );
}

// Exempt checks are skipped by triage, so they show no status, just the exemption.
export function ExemptLight({
  label,
  reason
}: {
  label: string;
  reason: string | null;
}): JSX.Element {
  return (
    <TrafficLight
      level="exempt"
      label={label}
      caption="Exempt"
      status={reason ? `exempt, ${reason}` : "exempt"}
    />
  );
}
